import type {FC, ReactElement} from 'react'
import {useEffect, useState} from 'react'
import {Box, CircularProgress, Divider, IconButton, Tooltip, Typography} from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import AddCommentIcon from '@mui/icons-material/AddComment'
import EditIcon from '@mui/icons-material/Edit'
import ConversationTitle from './ConversationTitle'
import InlineRename from './InlineRename'
import {useCustomTranslation} from '@hooks/useCustomTranslation.ts'
import {useUpdateConversationMutation} from '@apis/aiCoreAPI'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import {AlpinistTitle} from '@shared/components/alpinist/AlpinistTitle.tsx'
import {AlpinistDisclosure} from '@shared/components/alpinist/AlpinistDisclosure.tsx'

interface HeaderProps {
  onOpenHistory: () => void
  onNewConversation: () => Promise<void> | void
  /** A new conversation is being created: the button spins and ignores clicks. */
  creatingConversation?: boolean
  activeId?: string | null
  conversations: {id: string; title?: string}[]
  historyOpen?: boolean
  disabled?: boolean
  fullWidth?: boolean
  onToggleFullWidth?: () => void
  isMobile: boolean
  /**
   * Public demo embed: hide the conversation controls (rename, new conversation, history,
   * full-width). Leaves just the Alpinist branding — a lead-magnet is a single focused chat,
   * not an account.
   */
  minimalChrome?: boolean
}

const Header: FC<HeaderProps> = ({
  onOpenHistory,
  onNewConversation,
  creatingConversation = false,
  activeId,
  conversations,
  historyOpen = false,
  disabled = false,
  fullWidth = false,
  onToggleFullWidth = () => {},
  isMobile = false,
  minimalChrome = false,
}): ReactElement => {
  const currentTitle = conversations.find((c) => c.id === activeId)?.title
  const [t] = useCustomTranslation('alpinist')
  const [renaming, setRenaming] = useState(false)
  const [updateConversation] = useUpdateConversationMutation()

  // Leave edit mode if the active conversation changes underneath us.
  useEffect(() => {
    setRenaming(false)
  }, [activeId])

  const canRename = !disabled && !!activeId

  const handleRename = (next: string): void => {
    setRenaming(false)
    if (activeId) {
      void updateConversation({id: activeId, title: next})
    }
  }

  return (
    <>
      <Box
        component="header"
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          gap: 2,
        }}
      >
        {/* Left: app title + the AI disclosure it carries */}
        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <AlpinistTitle />
          <AlpinistDisclosure />
        </Box>
        {/* Center: dynamic conversation title (with inline rename) */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            overflow: 'hidden',
          }}
        >
          {!disabled &&
            !minimalChrome &&
            (renaming && activeId ? (
              <InlineRename
                value={currentTitle ?? ''}
                onSave={handleRename}
                onCancel={() => setRenaming(false)}
              />
            ) : (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{color: 'text.secondary', minWidth: 0, overflow: 'hidden'}}
                >
                  <ConversationTitle initialTitle={currentTitle} live />
                </Typography>
                {canRename && (
                  <Tooltip title={t('conversations_rename')}>
                    <IconButton
                      size="small"
                      onClick={() => setRenaming(true)}
                      aria-label={t('conversations_rename')}
                    >
                      <EditIcon fontSize="small" sx={{color: 'text.secondary'}} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ))}
        </Box>

        {/* Right: new + history buttons (hidden in the minimal demo embed) */}
        {!minimalChrome && (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
            <Tooltip
              title={
                disabled
                  ? ''
                  : creatingConversation
                    ? t('header_creating_conversation')
                    : t('header_new_conversation')
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={disabled || creatingConversation ? undefined : onNewConversation}
                  disabled={disabled || creatingConversation}
                  aria-label={t('header_new_conversation')}
                  aria-busy={creatingConversation}
                >
                  {creatingConversation ? (
                    <CircularProgress size={20} />
                  ) : (
                    <AddCommentIcon
                      fontSize="medium"
                      sx={{color: disabled ? 'text.disabled' : 'text.secondary'}}
                    />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={disabled ? '' : t('header_conversation_history')}>
              <span>
                <IconButton
                  size="small"
                  onClick={disabled ? undefined : onOpenHistory}
                  disabled={disabled}
                  aria-label={t('header_conversation_history')}
                  sx={{
                    bgcolor: !disabled && historyOpen ? 'action.hover' : 'transparent',
                    '&:hover': {
                      bgcolor: !disabled && historyOpen ? 'action.selected' : 'action.hover',
                    },
                  }}
                >
                  <HistoryIcon fontSize="medium" />
                </IconButton>
              </span>
            </Tooltip>
            {!isMobile && (
              <Tooltip title={fullWidth ? t('exit_full_width') : t('expand_full_width')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={onToggleFullWidth}
                    aria-label={'toggle_full_width'}
                  >
                    {fullWidth ? (
                      <CloseFullscreenIcon fontSize="small" />
                    ) : (
                      <OpenInFullIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      <Divider sx={{borderWidth: '1px'}} />
    </>
  )
}

export default Header
